import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { toast } from "sonner";
import { Calendar, Clock, Plus, Trash2, Check, X, Zap } from "lucide-react";
import { format, parseISO } from "date-fns";

function StatusBadge({ status }) {
  const cls = {
    pending: "bg-[#F4F1E9] text-[#1A1F1C] border-[#E6E2D8]",
    accepted: "bg-[#143122] text-[#FAFAFA] border-[#143122]",
    declined: "bg-white text-[#1A1F1C]/60 border-[#E6E2D8] line-through",
    cancelled: "bg-white text-[#1A1F1C]/50 border-[#E6E2D8] line-through",
  }[status] || "";
  return <span className={`kav-chip ${cls}`}>{status}</span>;
}

function toLocalDT(iso) {
  try { return format(parseISO(iso), "EEE d MMM · HH:mm"); } catch { return iso; }
}

export default function ProviderBookings() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ date: "", startTime: "09:00", endTime: "10:00", title: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    refresh();
  }, [user, authLoading, nav]);

  const refresh = async () => {
    try {
      const [s, b] = await Promise.all([
        api.get("/slots/mine"),
        api.get("/bookings/provider"),
      ]);
      setSlots(s.data);
      setBookings(b.data);
    } catch (err) { console.error("[provider-bookings] refresh failed", err); }
  };

  if (authLoading || !user) return null;

  const addSlot = async (e) => {
    e.preventDefault();
    if (!form.date || !form.startTime || !form.endTime) return;
    setBusy(true);
    try {
      const startISO = new Date(`${form.date}T${form.startTime}:00`).toISOString();
      const endISO = new Date(`${form.date}T${form.endTime}:00`).toISOString();
      if (new Date(endISO) <= new Date(startISO)) {
        toast.error("End must be after start");
        setBusy(false);
        return;
      }
      await api.post("/slots", { start: startISO, end: endISO, title: form.title || null, notes: form.notes || null });
      toast.success(t("slot_added"));
      setForm({ date: "", startTime: "09:00", endTime: "10:00", title: "", notes: "" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const removeSlot = async (slotId) => {
    if (!window.confirm("Remove this slot?")) return;
    try {
      await api.delete(`/slots/${slotId}`);
      toast.success(t("slot_removed"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const decide = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      toast.success(status === "accepted" ? t("booking_accepted") : t("booking_declined"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const pending = bookings.filter(b => b.status === "pending");
  const history = bookings.filter(b => b.status !== "pending");

  return (
    <div data-testid="provider-bookings-page" className="pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="overline text-[#143122] flex items-center gap-2">
          <Calendar size={12} strokeWidth={1.5} /> {t("bookings")}
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122]">
          {t("provider_bookings_title")}
        </h1>
        <p className="mt-3 text-sm text-[#1A1F1C]/70 max-w-2xl">
          {t("provider_bookings_sub")}
        </p>

        {/* Add slot */}
        <div className="mt-10 border border-[#E6E2D8] rounded-lg p-6 bg-white">
          <div className="overline text-[#143122]">{t("add_slot")}</div>
          <form onSubmit={addSlot} className="mt-4 grid md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1">{t("date")}</label>
              <input data-testid="slot-date" required type="date" className="kav-input" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">{t("start_time")}</label>
              <input data-testid="slot-start" required type="time" className="kav-input" value={form.startTime} onChange={e => setForm({...form, startTime: e.target.value})} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">{t("end_time")}</label>
              <input data-testid="slot-end" required type="time" className="kav-input" value={form.endTime} onChange={e => setForm({...form, endTime: e.target.value})} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1">{t("slot_title_label")}</label>
              <input data-testid="slot-title" className="kav-input" placeholder="Farrier visit, Vet check..." value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>
            <div className="md:col-span-6">
              <label className="block text-xs font-medium mb-1">{t("notes")}</label>
              <input data-testid="slot-notes" className="kav-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
            <button data-testid="add-slot-btn" disabled={busy} type="submit" className="kav-btn-primary md:col-span-6 justify-center">
              <Plus size={14} strokeWidth={1.75} /> {t("add_slot")}
            </button>
          </form>
        </div>

        {/* Slots list */}
        <div className="mt-10">
          <h2 className="font-serif-display text-2xl text-[#143122]">{t("my_slots")}</h2>
          <div className="mt-4 grid gap-3">
            {slots.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-10 text-center">
                {t("no_slots")}
              </div>
            ) : slots.map(s => (
              <div key={s.id} data-testid={`slot-row-${s.id}`} className="border border-[#E6E2D8] rounded-lg p-4 bg-white flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm text-[#1A1F1C]">
                    <Clock size={14} strokeWidth={1.5} className="text-[#143122]" />
                    <span className="font-medium">{toLocalDT(s.start)}</span>
                    <span className="text-[#1A1F1C]/50">→</span>
                    <span>{toLocalDT(s.end).split("· ")[1] || ""}</span>
                    {s.boosted_until && new Date(s.boosted_until) > new Date() && (
                      <span data-testid={`slot-boosted-${s.id}`} className="inline-flex items-center gap-1 kav-chip !bg-[#143122] !text-white !border-[#143122] !py-0">
                        <Zap size={10} strokeWidth={1.75} fill="#FAFAFA" /> {t("boosted")}
                      </span>
                    )}
                  </div>
                  {s.title && <div className="mt-1 font-serif-display text-lg text-[#143122]">{s.title}</div>}
                  {s.notes && <div className="text-xs text-[#1A1F1C]/60">{s.notes}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={s.status} />
                  <button
                    data-testid={`boost-slot-${s.id}`}
                    onClick={async () => {
                      try {
                        await api.post(`/slots/${s.id}/boost`);
                        toast.success(t("boosted_msg"));
                        refresh();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || t("error_generic"));
                      }
                    }}
                    className="text-[#143122]/70 hover:text-[#143122] transition-colors"
                    title={t("boost_urgent")}
                  >
                    <Zap size={16} strokeWidth={1.75} />
                  </button>
                  <button
                    data-testid={`delete-slot-${s.id}`}
                    onClick={() => removeSlot(s.id)}
                    className="text-[#1A1F1C]/60 hover:text-[#143122] transition-colors"
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending requests */}
        <div className="mt-12">
          <h2 className="font-serif-display text-2xl text-[#143122]">
            {t("pending_requests")} <span className="text-[#1A1F1C]/40">({pending.length})</span>
          </h2>
          <div className="mt-4 grid gap-3">
            {pending.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-10 text-center">
                {t("no_pending")}
              </div>
            ) : pending.map(b => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="border border-[#E6E2D8] rounded-lg p-4 bg-white">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="overline text-[#143122]">{b.client_name}</div>
                    <div className="mt-1 font-serif-display text-xl text-[#1A1F1C]">
                      {b.slot_title || t("booking")}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-[#1A1F1C]/70">
                      <Clock size={12} strokeWidth={1.5} /> {toLocalDT(b.slot_start)} — {toLocalDT(b.slot_end).split("· ")[1] || ""}
                    </div>
                    {b.message && <div className="mt-3 text-sm text-[#1A1F1C]/80 italic">&ldquo;{b.message}&rdquo;</div>}
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`accept-${b.id}`} onClick={() => decide(b.id, "accepted")} className="kav-btn-primary text-sm inline-flex items-center gap-1.5">
                      <Check size={14} strokeWidth={1.75} /> {t("accept")}
                    </button>
                    <button data-testid={`decline-${b.id}`} onClick={() => decide(b.id, "declined")} className="kav-btn-secondary text-sm inline-flex items-center gap-1.5">
                      <X size={14} strokeWidth={1.75} /> {t("decline")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="mt-12">
            <h2 className="font-serif-display text-2xl text-[#143122]">{t("history")}</h2>
            <div className="mt-4 grid gap-2">
              {history.map(b => (
                <div key={b.id} className="border border-[#E6E2D8] rounded-lg p-3 bg-white flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="text-[#1A1F1C]">{b.client_name}</span>
                    <span className="text-[#1A1F1C]/60 text-xs">{toLocalDT(b.slot_start)}</span>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
